import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, AlertCircle, Send, Camera, X, Copy, Search } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { resolveContentHtml } from "@/lib/url";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FALLBACK_CATEGORIES = [
    { value: "winda", label: "Winda" },
    { value: "oswietlenie", label: "Oświetlenie" },
    { value: "halas", label: "Hałas" },
    { value: "parking", label: "Parking" },
    { value: "porzadek", label: "Porządek / sprzątanie" },
    { value: "elewacja", label: "Elewacja / klatka schodowa" },
    { value: "teren_wspolny", label: "Teren wspólny" },
    { value: "garaz_wspolny_ii", label: "Garaż wspólny II" },
    { value: "inne", label: "Inne" },
];

const TrackingLinkBox = ({ path }) => {
    const url = `${window.location.origin}${path}`;
    const copy = async () => {
        try { await navigator.clipboard.writeText(url); toast.success("Link skopiowany."); }
        catch { toast.error("Nie udało się skopiować – zaznacz link ręcznie."); }
    };
    return (
        <div className="my-6 text-left bg-white border border-emerald-300 rounded-2xl p-5" data-testid="report-tracking-box">
            <p className="text-sm font-semibold text-[#092E22] mb-1">Twój prywatny link do śledzenia statusu</p>
            <p className="text-xs text-gray-500 mb-3">Zapisz go – dzięki niemu sprawdzisz postęp, dodasz informacje i ocenisz realizację.</p>
            <div className="flex flex-col sm:flex-row gap-2">
                <input readOnly value={url} onFocus={(e) => e.target.select()} data-testid="report-tracking-url"
                    className="flex-1 min-w-0 h-11 px-4 rounded-full border border-gray-200 bg-[#FAF8F3] font-mono text-xs text-[#0D4F3C]" />
                <button type="button" onClick={copy} data-testid="report-tracking-copy"
                    className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full border border-[#0D4F3C] text-[#0D4F3C] text-sm font-medium hover:bg-[#0D4F3C]/5">
                    <Copy size={14} /> Kopiuj
                </button>
                <Link to={path} data-testid="report-tracking-open"
                    className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-[#D4A537] text-[#092E22] text-sm font-semibold hover:bg-[#c4952b]">
                    <Search size={14} /> Śledź status
                </Link>
            </div>
        </div>
    );
};

export default function ReportPage() {
    const [trackingPath, setTrackingPath] = useState("");
    const [form, setForm] = useState({
        name: "", email: "", phone: "",
        category: "inne", location: "", description: "",
        priority: "",
        website: "", // honeypot
    });
    const [images, setImages] = useState([]); // {url, uploading, name}
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [ticketNumber, setTicketNumber] = useState("");
    const [intro, setIntro] = useState("");
    const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
    const fileInput = useRef(null);

    useEffect(() => {
        api.get("/settings").then((r) => setIntro(r.data?.report_intro_html || "")).catch(() => {});
        api.get("/report-categories/public").then((r) => {
            if (Array.isArray(r.data) && r.data.length > 0) {
                setCategories(r.data.map((c) => ({ value: c.key, label: c.label })));
            }
        }).catch(() => {});
    }, []);

    const uploadImages = async (ev) => {
        const files = Array.from(ev.target.files || []);
        if (!files.length) return;
        if (images.length + files.length > 5) {
            toast.error("Maksymalnie 5 zdjęć.");
            ev.target.value = "";
            return;
        }
        for (const file of files) {
            if (!file.type.startsWith("image/")) { toast.error(`"${file.name}" to nie zdjęcie.`); continue; }
            if (file.size > 10 * 1024 * 1024) { toast.error(`"${file.name}" > 10MB.`); continue; }
            const placeholder = { url: "", uploading: true, name: file.name };
            setImages((prev) => [...prev, placeholder]);
            try {
                const fd = new FormData();
                fd.append("file", file);
                const { data } = await api.post("/upload-public", fd, { headers: { "Content-Type": "multipart/form-data" } });
                setImages((prev) => prev.map((img) => img === placeholder ? { url: data.url, uploading: false, name: file.name } : img));
            } catch (err) {
                setImages((prev) => prev.filter((img) => img !== placeholder));
                toast.error(formatApiErrorDetail(err.response?.data?.detail) || `Nie udało się wysłać "${file.name}".`);
            }
        }
        ev.target.value = "";
    };

    const removeImage = (idx) => setImages((prev) => prev.filter((_, i) => i !== idx));

    const submit = async (e) => {
        e.preventDefault();
        if (images.some((i) => i.uploading)) {
            toast.error("Poczekaj aż zdjęcia zostaną wysłane.");
            return;
        }
        setLoading(true);
        try {
            const payload = {
                ...form,
                images: images.filter((i) => i.url).map((i) => i.url),
                priority: form.priority || null,
            };
            const { data } = await api.post("/reports", payload);
            setSubmitted(true);
            setTicketNumber(data?.ticket_number || "");
            setTrackingPath(data?.tracking_path || "");
            toast.success(data.email_sent ? "Zgłoszenie wysłane do administracji." : "Zgłoszenie przyjęte – widoczne w panelu admina.");
        } catch (err) {
            const detail = err.response?.data?.detail;
            if (err.response?.status === 429) {
                toast.error("Zbyt wiele zgłoszeń. Spróbuj za chwilę.");
            } else {
                toast.error(formatApiErrorDetail(detail) || "Nie udało się wysłać zgłoszenia.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="report-page" className="min-h-screen bg-[#FDFDFD]">
            <Navbar />
            <header className="bg-[#092E22] text-white pt-32 pb-16 relative overflow-hidden">
                <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-[#D4A537]/15 blur-3xl" />
                <div className="max-w-4xl mx-auto px-6 lg:px-10 relative z-10">
                    <Link to="/" className="inline-flex items-center gap-2 text-[#D4A537] text-sm hover:underline">
                        <ArrowLeft size={14} /> Powrót na stronę główną
                    </Link>
                    <div className="flex items-center gap-4 mt-6 mb-4">
                        <div className="w-14 h-14 rounded-2xl bg-red-500/20 text-red-300 flex items-center justify-center">
                            <AlertCircle size={26} />
                        </div>
                        <p className="text-[#D4A537] uppercase tracking-[0.25em] text-xs font-semibold">Zgłoszenie</p>
                    </div>
                    <h1 className="font-serif text-4xl md:text-5xl leading-tight">Zgłoś usterkę</h1>
                    <p className="text-white/75 mt-3 max-w-2xl">
                        Zauważyłeś/aś problem na osiedlu? Wypełnij formularz – Twoje zgłoszenie trafi bezpośrednio do administracji wspólnoty.
                    </p>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
                {intro && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="bg-[#0D4F3C]/5 border border-[#D4A537]/30 rounded-3xl p-7 md:p-9 mb-8 rich-content text-gray-700"
                        dangerouslySetInnerHTML={{ __html: resolveContentHtml(intro) }}
                        data-testid="report-intro"
                    />
                )}
                {submitted ? (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-emerald-50 border border-emerald-200 rounded-3xl p-10 text-center"
                        data-testid="report-success"
                    >
                        <h2 className="font-serif text-3xl text-[#092E22] mb-3">Dziękujemy za zgłoszenie!</h2>
                        {ticketNumber && (
                            <div className="inline-block my-4 px-5 py-3 rounded-2xl bg-white border border-emerald-300" data-testid="report-ticket-number">
                                <p className="text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-1">Numer zgłoszenia</p>
                                <p className="font-mono text-lg font-bold text-[#0D4F3C] tracking-wider">{ticketNumber}</p>
                            </div>
                        )}
                        <p className="text-gray-700 mb-2">Twoje zgłoszenie zostało zarejestrowane i przekazane do administratorów wspólnoty.</p>
                        {trackingPath && <TrackingLinkBox path={trackingPath} />}
                        <p className="text-gray-500 text-sm mb-6">Ten sam link wysłaliśmy w potwierdzeniu na podany adres e-mail (sprawdź też folder SPAM).</p>
                        <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0D4F3C] text-white font-medium hover:bg-[#092E22]">
                            Wróć na stronę główną
                        </Link>
                    </motion.div>
                ) : (
                    <motion.form
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        onSubmit={submit}
                        className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 md:p-12 space-y-5"
                        data-testid="report-form"
                    >
                        {/* Honeypot */}
                        <input
                            type="text"
                            name="website"
                            value={form.website}
                            onChange={(e) => setForm({ ...form, website: e.target.value })}
                            tabIndex={-1}
                            autoComplete="off"
                            style={{ position: "absolute", left: "-10000px", opacity: 0, pointerEvents: "none" }}
                            aria-hidden="true"
                        />
                        <div className="grid md:grid-cols-2 gap-5">
                            <div className="space-y-2">
                                <Label>Imię i nazwisko *</Label>
                                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jan Kowalski" data-testid="report-name" className="h-12 rounded-full px-5" />
                            </div>
                            <div className="space-y-2">
                                <Label>E-mail *</Label>
                                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jan@example.com" data-testid="report-email" className="h-12 rounded-full px-5" />
                            </div>
                            <div className="space-y-2">
                                <Label>Telefon (opcjonalnie)</Label>
                                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+48 ..." data-testid="report-phone" className="h-12 rounded-full px-5" />
                            </div>
                            <div className="space-y-2">
                                <Label>Kategoria *</Label>
                                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                    <SelectTrigger className="h-12 rounded-full px-5" data-testid="report-category"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {categories.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>{'Lokalizacja (np. „Budynek 25E, klatka 3, 4 piętro")'}</Label>
                            <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Wskaż miejsce" data-testid="report-location" className="h-12 rounded-full px-5" />
                        </div>
                        <div className="space-y-2">
                            <Label>Opis problemu *</Label>
                            <Textarea required rows={6} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Opisz szczegółowo problem..." data-testid="report-description" className="rounded-2xl px-5 py-4" />
                        </div>

                        {/* Priority + Images */}
                        <div className="grid md:grid-cols-2 gap-5">
                            <div className="space-y-2">
                                <Label>Priorytet</Label>
                                <Select value={form.priority || "auto"} onValueChange={(v) => setForm({ ...form, priority: v === "auto" ? "" : v })}>
                                    <SelectTrigger className="h-12 rounded-full px-5" data-testid="report-priority"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="auto">Automatycznie (zalecane)</SelectItem>
                                        <SelectItem value="low">Niski — kosmetyka, nie pilne</SelectItem>
                                        <SelectItem value="normal">Normalny</SelectItem>
                                        <SelectItem value="high">Wysoki — zepsute, utrudnia życie</SelectItem>
                                        <SelectItem value="critical">⚠ Krytyczny — zagrożenie (gaz, woda, prąd)</SelectItem>
                                    </SelectContent>
                                </Select>
                                <p className="text-[11px] text-gray-500">„Automatycznie" — system odgaduje priorytet z opisu.</p>
                            </div>
                            <div className="space-y-2">
                                <Label>Zdjęcia (opcjonalnie, max 5)</Label>
                                <div className="flex flex-wrap gap-2 items-start">
                                    {images.map((img, idx) => (
                                        <div key={idx} className="relative w-20 h-20 rounded-xl overflow-hidden bg-gray-100 border border-gray-200 group" data-testid={`report-image-${idx}`}>
                                            {img.uploading ? (
                                                <div className="w-full h-full flex items-center justify-center text-[10px] text-gray-400">...</div>
                                            ) : (
                                                <img src={img.url} alt="" className="w-full h-full object-cover" />
                                            )}
                                            <button type="button" onClick={() => removeImage(idx)} className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                <X size={10} />
                                            </button>
                                        </div>
                                    ))}
                                    {images.length < 5 && (
                                        <button type="button" onClick={() => fileInput.current?.click()} data-testid="report-image-upload" className="w-20 h-20 rounded-xl border-2 border-dashed border-gray-300 hover:border-[#0D4F3C] text-gray-400 hover:text-[#0D4F3C] flex flex-col items-center justify-center gap-1 transition-colors">
                                            <Camera size={18} />
                                            <span className="text-[9px]">Dodaj</span>
                                        </button>
                                    )}
                                </div>
                                <input ref={fileInput} type="file" accept="image/*" multiple onChange={uploadImages} className="hidden" />
                                <p className="text-[11px] text-gray-500">JPG/PNG do 10MB. Zdjęcia pomogą wykonawcy od razu zrozumieć problem.</p>
                            </div>
                        </div>

                        <Button type="submit" disabled={loading} data-testid="report-submit" className="bg-[#0D4F3C] hover:bg-[#092E22] text-white rounded-full h-12 px-8 font-semibold">
                            {loading ? "Wysyłanie..." : (<><Send size={16} className="mr-2" /> Wyślij zgłoszenie</>)}
                        </Button>
                        <p className="text-xs text-gray-500">Twoje dane przetwarzamy zgodnie z polityką prywatności (RODO).</p>
                    </motion.form>
                )}
            </main>

            <Footer />
        </div>
    );
}
